/**
 * Order Edit Modal
 * Allows cashiers to edit any unpaid order:
 * - Add new items from menu
 * - Change quantities
 * - Remove items
 * - Apply discounts
 * - View running totals
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  X, Search, Plus, Minus, Trash2, Percent, ShoppingBag, 
  RefreshCw, Save, AlertCircle, Check, ChevronDown, ChevronUp,
  Coffee, UtensilsCrossed, IceCream, Croissant, MapPin
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

// Category icons mapping
const CATEGORY_ICONS = {
  'coffee': Coffee,
  'breakfast': UtensilsCrossed,
  'lunch': UtensilsCrossed,
  'iced-drinks': IceCream,
  'smoothies-blends': IceCream,
  'pastries': Croissant,
  'default': ShoppingBag
};

export default function OrderEditModal({ 
  order, 
  onClose, 
  onOrderUpdated, 
  staffId,
  channelConfig 
}) {
  // State
  const [editedItems, setEditedItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [menuItems, setMenuItems] = useState([]);
  const [filteredMenu, setFilteredMenu] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [menuError, setMenuError] = useState(null); // Track menu fetch errors
  const [saving, setSaving] = useState(false);
  const [showMenuBrowser, setShowMenuBrowser] = useState(false);
  const [showDiscountPanel, setShowDiscountPanel] = useState(false);
  const [discountPercent, setDiscountPercent] = useState(order?.discount_percent || 0);
  const [pendingAdditions, setPendingAdditions] = useState([]);
  const [quantityChanges, setQuantityChanges] = useState({}); // Track quantity changes for existing items { itemIndex: newQuantity }

  // Initialize with current order items
  useEffect(() => {
    if (order?.items) {
      setEditedItems(order.items.map((item, idx) => ({
        ...item,
        _localId: `existing-${idx}`,
        _isExisting: true
      })));
      setDiscountPercent(order.discount_percent || 0);
    }
  }, [order]);

  // Fetch menu items with retry capability
  const fetchMenu = useCallback(async () => {
    setLoading(true);
    setMenuError(null);
    try {
      const [menuRes, catRes] = await Promise.all([
        api.get('/menu'),
        api.get('/menu/categories')
      ]);
      setMenuItems(menuRes.data.items || menuRes.data || []);
      setCategories(catRes.data.categories || catRes.data || []);
      setFilteredMenu(menuRes.data.items || menuRes.data || []);
    } catch (err) {
      console.error('Failed to fetch menu:', err);
      const errorMsg = err.code === 'ECONNABORTED' || err.message?.includes('timeout')
        ? 'Menu loading timed out. Please try again.'
        : 'Failed to load menu items. Please try again.';
      setMenuError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMenu();
  }, [fetchMenu]);

  // Filter menu by search and category
  useEffect(() => {
    let filtered = menuItems;
    
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(item => item.category === selectedCategory);
    }
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item => 
        item.name.toLowerCase().includes(query) ||
        item.category?.toLowerCase().includes(query)
      );
    }
    
    setFilteredMenu(filtered);
  }, [searchQuery, selectedCategory, menuItems]);

  // Calculate totals
  const calculateTotals = useCallback(() => {
    const subtotal = editedItems.reduce((sum, item) => {
      return sum + (item.price || 0) * (item.quantity || 1);
    }, 0);
    
    const discountAmount = subtotal * (discountPercent / 100);
    const total = subtotal - discountAmount;
    
    return { subtotal, discountAmount, total };
  }, [editedItems, discountPercent]);

  const { subtotal, discountAmount, total } = calculateTotals();

  // Add item from menu
  const addItemToOrder = (menuItem) => {
    // Get price from first size or base price
    let price = menuItem.price || 0;
    let selectedSize = null;
    
    if (menuItem.sizes?.length > 0) {
      selectedSize = menuItem.sizes[0];
      price = selectedSize.price || price;
    }
    
    const newItem = {
      _localId: `new-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      _isNew: true,
      item_id: menuItem.id,
      name: menuItem.name,
      price: price,
      quantity: 1,
      category: menuItem.category,
      size: selectedSize,
      selectedSize: selectedSize
    };
    
    setEditedItems(prev => [...prev, newItem]);
    setPendingAdditions(prev => [...prev, newItem]);
    toast.success(`Added ${menuItem.name}`);
  };

  // Update item quantity
  const updateQuantity = (localId, delta, itemIndex, isExisting) => {
    setEditedItems(prev => prev.map(item => {
      if (item._localId === localId) {
        const newQty = Math.max(1, (item.quantity || 1) + delta);
        return { ...item, quantity: newQty };
      }
      return item;
    }));
    
    // Track quantity changes for existing items (to sync with backend)
    if (isExisting && itemIndex !== undefined) {
      const item = editedItems.find(i => i._localId === localId);
      const newQty = Math.max(1, (item?.quantity || 1) + delta);
      setQuantityChanges(prev => ({
        ...prev,
        [itemIndex]: newQty
      }));
    }
  };

  // Remove item - handles both new and existing items
  const removeItem = async (localId, isExisting, itemIndex) => {
    const item = editedItems.find(i => i._localId === localId);
    
    if (isExisting) {
      // For existing items, call the backend API to remove
      try {
        const reason = window.prompt(`Reason for removing "${item?.name}"? (Manager approval required)`);
        if (reason === null) return; // User cancelled
        
        const headers = { 'X-Staff-ID': staffId };
        const res = await api.post('/api/pos/order/remove-item', {
          order_id: order.id,
          item_index: itemIndex,
          reason: reason || 'No reason provided'
        }, { headers, timeout: 10000 });
        
        if (res.data.success) {
          toast.success(`Removed ${item?.name}. New total: $${res.data.new_total?.toFixed(2)}`);
          // Update local state
          setEditedItems(prev => prev.filter(i => i._localId !== localId));
          // Refresh order data
          onOrderUpdated?.();
        }
      } catch (err) {
        if (err.response?.status === 403) {
          toast.error('Only managers/owners can remove existing items');
        } else {
          toast.error('Failed to remove item: ' + (err.response?.data?.detail || err.message));
        }
      }
    } else {
      // For newly added items, just remove from pending
      setPendingAdditions(prev => prev.filter(i => i._localId !== localId));
      setEditedItems(prev => prev.filter(i => i._localId !== localId));
      toast.info(`Removed ${item?.name}`);
    }
  };

  // Save changes
  const saveChanges = async () => {
    setSaving(true);
    
    try {
      const headers = { 'X-Staff-ID': staffId };
      let hasChanges = false;
      let successMessages = [];
      
      // Apply quantity changes for existing items
      const quantityChangeEntries = Object.entries(quantityChanges);
      if (quantityChangeEntries.length > 0) {
        for (const [itemIndex, newQuantity] of quantityChangeEntries) {
          try {
            const qtyRes = await api.post('/api/pos/order/update-item-quantity', {
              order_id: order.id,
              item_index: parseInt(itemIndex),
              new_quantity: newQuantity
            }, { headers, timeout: 10000 });
            
            if (qtyRes.data.success) {
              hasChanges = true;
            }
          } catch (err) {
            console.error(`Failed to update quantity for item ${itemIndex}:`, err);
            toast.error(`Failed to update quantity: ${err.response?.data?.detail || err.message}`);
          }
        }
        if (hasChanges) {
          successMessages.push(`Updated ${quantityChangeEntries.length} item quantity(s)`);
        }
      }
      
      // Apply discount if changed
      if (discountPercent !== (order?.discount_percent || 0)) {
        const discountRes = await api.post('/api/pos/order/apply-discount', {
          order_id: order.id,
          discount_percent: discountPercent,
          discount_type: 'manual'
        }, { headers, timeout: 10000 });
        
        if (discountRes.data.success) {
          successMessages.push(`Discount: ${discountPercent}%`);
          hasChanges = true;
        }
      }
      
      // Add new items
      if (pendingAdditions.length > 0) {
        const itemsToAdd = pendingAdditions.map(item => ({
          item_id: item.item_id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          category: item.category,
          size: item.size,
          selectedSize: item.selectedSize,
          special_instructions: item.special_instructions
        }));
        
        const addRes = await api.post('/api/pos/order/add-items', {
          order_id: order.id,
          items: itemsToAdd
        }, { headers, timeout: 10000 });
        
        if (addRes.data.success) {
          successMessages.push(`Added ${pendingAdditions.length} item(s)`);
          hasChanges = true;
        }
      }
      
      // Show success and close
      if (hasChanges) {
        toast.success(`Order updated: ${successMessages.join(', ')}`);
        onOrderUpdated?.();
      } else {
        toast.info('No changes to save');
      }
      
      // Close modal regardless of changes (user clicked save)
      onClose();
    } catch (err) {
      console.error('Failed to save order changes:', err);
      toast.error('Failed to save: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  // Get category icon
  const getCategoryIcon = (category) => {
    const Icon = CATEGORY_ICONS[category] || CATEGORY_ICONS.default;
    return Icon;
  };

  if (!order) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
      <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-4xl max-h-[85vh] flex flex-col shadow-2xl overflow-hidden">
        {/* Header */}
        <div className={`p-4 ${channelConfig?.bgColor || 'bg-gray-800'} text-white flex items-center justify-between`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <ShoppingBag className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Edit Order #{order.order_number || order.id?.slice(0, 8).toUpperCase()}</h2>
              <div className="flex items-center gap-2 text-sm opacity-90">
                <span>{order.customer_name || 'Guest'}</span>
                {order.table_number && (
                  <span className="flex items-center gap-1 bg-white/20 px-2 py-0.5 rounded">
                    <MapPin className="w-3 h-3" />
                    Table {order.table_number}
                  </span>
                )}
              </div>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
            data-testid="close-edit-modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content - constrained height with proper overflow */}
        <div className="flex-1 min-h-0 overflow-hidden flex">
          {/* Left: Current Items */}
          <div className="flex-1 min-h-0 flex flex-col border-r border-gray-200 dark:border-gray-700">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 dark:text-white">Order Items</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowMenuBrowser(!showMenuBrowser)}
                className="text-sm"
                data-testid="toggle-menu-browser"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Items
              </Button>
            </div>
            
            {/* Items List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {editedItems.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  <ShoppingBag className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>No items in order</p>
                </div>
              ) : (
                editedItems.map((item, idx) => {
                  const ItemIcon = getCategoryIcon(item.category);
                  return (
                    <div 
                      key={item._localId}
                      className={`flex items-center gap-3 p-3 rounded-lg border ${
                        item._isNew 
                          ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' 
                          : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                      }`}
                      data-testid={`edit-item-${item._localId}`}
                    >
                      <div className="w-10 h-10 rounded-lg bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                        <ItemIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white truncate">
                          {item.name}
                          {item._isNew && (
                            <span className="ml-2 text-xs bg-green-500 text-white px-1.5 py-0.5 rounded">NEW</span>
                          )}
                        </p>
                        <p className="text-sm text-gray-500">
                          ${item.price?.toFixed(2)} each
                          {item.size?.name && ` • ${item.size.name}`}
                        </p>
                        {item.special_instructions && (
                          <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                            Note: {item.special_instructions}
                          </p>
                        )}
                      </div>
                      
                      {/* Quantity Controls */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateQuantity(item._localId, -1, idx, item._isExisting)}
                          disabled={item.quantity <= 1}
                          className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                          data-testid={`decrease-qty-${item._localId}`}
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="w-8 text-center font-bold text-gray-900 dark:text-white">
                          {item.quantity || 1}
                        </span>
                        <button
                          onClick={() => updateQuantity(item._localId, 1, idx, item._isExisting)}
                          className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center hover:bg-gray-300 dark:hover:bg-gray-600"
                          data-testid={`increase-qty-${item._localId}`}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                      
                      {/* Item Total */}
                      <div className="text-right w-20">
                        <p className="font-bold text-gray-900 dark:text-white">
                          ${((item.price || 0) * (item.quantity || 1)).toFixed(2)}
                        </p>
                      </div>
                      
                      {/* Remove Button - Works for all items (manager approval for existing) */}
                      <button
                        onClick={() => removeItem(item._localId, item._isExisting, idx)}
                        className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                          item._isNew 
                            ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50'
                            : 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 hover:bg-orange-200 dark:hover:bg-orange-900/50'
                        }`}
                        data-testid={`remove-item-${item._localId}`}
                        title={item._isNew ? 'Remove this item' : 'Remove item (Manager approval required)'}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Right: Menu Browser (Collapsible) */}
          {showMenuBrowser && (
            <div className="w-80 min-h-0 flex flex-col bg-gray-50 dark:bg-gray-800/50">
              <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={loading ? "Loading menu..." : "Search menu..."}
                    disabled={loading}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="menu-search-input"
                  />
                </div>
                
                {/* Category Filter */}
                <div className="flex gap-1 mt-2 overflow-x-auto pb-1">
                  <button
                    onClick={() => setSelectedCategory('all')}
                    disabled={loading}
                    className={`px-3 py-1 text-xs font-medium rounded-full whitespace-nowrap disabled:opacity-50 ${
                      selectedCategory === 'all'
                        ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    All
                  </button>
                  {categories.slice(0, 6).map(cat => (
                    <button
                      key={cat.id || cat}
                      onClick={() => setSelectedCategory(cat.id || cat)}
                      className={`px-3 py-1 text-xs font-medium rounded-full whitespace-nowrap ${
                        selectedCategory === (cat.id || cat)
                          ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {cat.name || cat}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Menu Items */}
              <div className="flex-1 overflow-y-auto p-2 space-y-1">
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-gray-400 mb-2" />
                    <p className="text-sm text-gray-500">Loading menu items...</p>
                  </div>
                ) : menuError ? (
                  <div className="flex flex-col items-center justify-center py-8">
                    <AlertCircle className="w-8 h-8 text-red-400 mb-2" />
                    <p className="text-sm text-red-500 text-center mb-3">{menuError}</p>
                    <button
                      onClick={fetchMenu}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-lg transition-colors"
                      data-testid="retry-menu-load"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Retry
                    </button>
                  </div>
                ) : filteredMenu.length === 0 ? (
                  <div className="text-center text-gray-500 py-8">
                    <Search className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">{searchQuery ? 'No matching items' : 'No items available'}</p>
                  </div>
                ) : (
                  filteredMenu.slice(0, 50).map((item) => {
                    const ItemIcon = getCategoryIcon(item.category);
                    const price = item.sizes?.[0]?.price || item.price || 0;
                    return (
                      <button
                        key={item.id}
                        onClick={() => addItemToOrder(item)}
                        className="w-full flex items-center gap-2 p-2 rounded-lg bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 text-left transition-colors"
                        data-testid={`add-menu-item-${item.id}`}
                      >
                        <div className="w-8 h-8 rounded bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                          <ItemIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{item.name}</p>
                          <p className="text-xs text-gray-500">{item.category}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-gray-900 dark:text-white">${price.toFixed(2)}</span>
                          <Plus className="w-4 h-4 text-green-500" />
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer: Totals & Actions */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          {/* Discount Panel */}
          <div className="mb-4">
            <button
              onClick={() => setShowDiscountPanel(!showDiscountPanel)}
              className="flex items-center gap-2 text-sm font-medium text-purple-600 dark:text-purple-400 hover:text-purple-700"
              data-testid="toggle-discount-panel"
            >
              <Percent className="w-4 h-4" />
              {discountPercent > 0 ? `Discount: ${discountPercent}%` : 'Apply Discount'}
              {showDiscountPanel ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            
            {showDiscountPanel && (
              <div className="mt-2 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                <div className="grid grid-cols-6 gap-1">
                  {[0, 5, 10, 15, 20, 50].map(pct => (
                    <button
                      key={pct}
                      onClick={() => setDiscountPercent(pct)}
                      className={`py-2 text-sm font-bold rounded transition-all ${
                        discountPercent === pct
                          ? 'bg-purple-500 text-white'
                          : 'bg-white dark:bg-gray-800 border border-purple-300 dark:border-purple-600 text-purple-600 dark:text-purple-400 hover:bg-purple-100'
                      }`}
                      data-testid={`discount-${pct}`}
                    >
                      {pct === 0 ? 'None' : `${pct}%`}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Totals */}
          <div className="flex items-end justify-between mb-4">
            <div className="space-y-1">
              <div className="flex items-center gap-4 text-sm">
                <span className="text-gray-500">Subtotal:</span>
                <span className="font-medium text-gray-900 dark:text-white">${subtotal.toFixed(2)}</span>
              </div>
              {discountPercent > 0 && (
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-green-600 dark:text-green-400">Discount ({discountPercent}%):</span>
                  <span className="font-medium text-green-600 dark:text-green-400">-${discountAmount.toFixed(2)}</span>
                </div>
              )}
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Total</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">${total.toFixed(2)}</p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1"
              data-testid="cancel-edit-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={saveChanges}
              disabled={saving}
              className={`flex-1 ${channelConfig?.bgColor || 'bg-gray-800'} hover:opacity-90 text-white`}
              data-testid="save-order-btn"
            >
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
          
          {/* Pending changes indicator */}
          {(pendingAdditions.length > 0 || discountPercent !== (order?.discount_percent || 0)) && (
            <p className="text-xs text-center text-gray-500 mt-2">
              {pendingAdditions.length > 0 && `${pendingAdditions.length} item(s) to add`}
              {pendingAdditions.length > 0 && discountPercent !== (order?.discount_percent || 0) && ' • '}
              {discountPercent !== (order?.discount_percent || 0) && `Discount changed to ${discountPercent}%`}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
